import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, CheckCircle2, Clock, Ticket as TicketIcon, BarChart3, ListFilter } from 'lucide-react';
import { ticketApi } from './api';
import type { Ticket, TicketStats } from './api';
import StatusBadge from './StatusBadge';
import SlaPill from './SlaPill';

export default function OpsDashboard() {
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [error, setError] = useState<string | null>(null);

  async function loadDashboard() {
    try {
      const [summary, list] = await Promise.all([ticketApi.getStats(), ticketApi.listTickets()]);
      setStats(summary);
      setTickets(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load dashboard');
    }
  }

  useEffect(() => {
    loadDashboard();
  }, []);

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto w-full px-4 py-8">
        <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="max-w-[1400px] mx-auto w-full px-4 py-8 flex items-center justify-center min-h-[50vh]">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent" />
        <span className="ml-3 text-slate-500 font-medium">Loading dashboard...</span>
      </div>
    );
  }

  const active = tickets
    .filter((ticket) => ticket.sla.isActive)
    .sort((a, b) => a.sla.hoursToResolutionDue - b.sla.hoursToResolutionDue);

  return (
    <div className="max-w-[1400px] mx-auto w-full px-4 py-8 space-y-6">
      
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Operations dashboard</h2>
        <p className="text-sm text-slate-500 mt-1">Overview of ticket volume, statuses, and SLA compliance.</p>
      </div>

      {/* KPI Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Total tickets"
          value={stats.total}
          icon={<TicketIcon size={20} />}
          color="blue"
        />
        <StatCard
          label="Open"
          value={stats.open}
          icon={<Activity size={20} />}
          color="indigo"
        />
        <StatCard
          label="Resolved"
          value={stats.resolved}
          icon={<CheckCircle2 size={20} />}
          color="green"
        />
        <StatCard
          label="SLA at risk"
          value={stats.slaAtRisk}
          icon={<Clock size={20} />}
          color="amber"
        />
        <StatCard
          label="SLA breached"
          value={stats.slaBreached}
          icon={<AlertTriangle size={20} />}
          color="red"
        />
      </div>

      {/* Breakdown Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader icon={<BarChart3 size={18} className="text-blue-600" />} title="Tickets by status" />
          <BreakdownBars data={stats.byStatus} colorClass="bg-blue-500" />
        </Card>
        <Card>
          <CardHeader icon={<ListFilter size={18} className="text-purple-600" />} title="Tickets by category" />
          <BreakdownBars data={stats.byCategory} colorClass="bg-purple-500" />
        </Card>
      </div>

      {/* SLA Watch Table */}
      <Card>
        <CardHeader icon={<Activity size={18} className="text-blue-600" />} title="Active tickets - SLA watch" />
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Subject</th>
                <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Priority</th>
                <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Assigned</th>
                <th className="py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">SLA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {active.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-500">
                    No active tickets at the moment.
                  </td>
                </tr>
              )}
              {active.map((ticket) => {
                const isBreached = ticket.sla.resolutionState === 'breached';
                const isAtRisk = ticket.sla.resolutionState === 'at_risk';
                
                return (
                  <tr 
                    key={ticket.id} 
                    className={`transition-colors hover:bg-slate-50/50 ${
                      isBreached ? 'bg-red-50/30' : isAtRisk ? 'bg-amber-50/30' : ''
                    }`}
                  >
                    <td className="py-4 px-4 text-sm font-medium">
                      <Link to={`/tickets/view/${ticket.id}`} className="text-blue-600 hover:text-blue-800 hover:underline">
                        {ticket.reference}
                      </Link>
                    </td>
                    <td className="py-4 px-4 text-sm text-slate-800 font-medium">{ticket.subject}</td>
                    <td className="py-4 px-4 text-sm text-slate-600">{ticket.category}</td>
                    <td className="py-4 px-4 text-sm text-slate-600 font-medium">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                        ticket.priority === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                        ticket.priority === 'HIGH' ? 'bg-orange-100 text-orange-700' :
                        ticket.priority === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm">
                      <StatusBadge status={ticket.status} />
                    </td>
                    <td className="py-4 px-4 text-sm text-slate-500">
                      {ticket.assignedTo || '-'}
                    </td>
                    <td className="py-4 px-4 text-sm">
                      <SlaPill sla={ticket.sla} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ---------- Sub-components ----------

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border border-slate-200 overflow-hidden">
      {children}
    </div>
  );
}

function CardHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    </div>
  );
}

function StatCard({ label, value, icon, color }: { label: string; value: number; icon: React.ReactNode; color: string }) {
  const colorMap: Record<string, { bg: string, text: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600' },
    green: { bg: 'bg-green-50', text: 'text-green-600' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
    red: { bg: 'bg-red-50', text: 'text-red-600' },
  };

  const styles = colorMap[color] || colorMap.blue;

  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.1)] border border-slate-200 flex flex-col justify-between">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-xl ${styles.bg} ${styles.text} flex items-center justify-center shrink-0`}>
          {icon}
        </div>
      </div>
      <div>
        <div className="text-3xl font-bold text-slate-900">{value}</div>
        <div className="text-sm font-medium text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

function BreakdownBars({ data, colorClass }: { data: Record<string, number>; colorClass: string }) {
  const entries = Object.entries(data).filter(([, value]) => value > 0);
  const max = Math.max(1, ...entries.map(([, value]) => value));

  if (!entries.length) {
    return <p className="p-6 text-sm text-slate-500 text-center">No data available.</p>;
  }

  return (
    <div className="p-6 space-y-5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center gap-4">
          <span className="w-32 text-sm font-medium text-slate-700 capitalize truncate" title={key.replace('_', ' ')}>
            {key.replace(/_/g, ' ')}
          </span>
          <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${colorClass} transition-all duration-500`}
              style={{ width: `${(value / max) * 100}%` }}
            />
          </div>
          <span className="w-8 text-sm font-semibold text-slate-700 text-right">
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}